import type { User } from 'firebase/auth';
import { createContext } from 'react';

/**
 * Auth context shape. Kept in its OWN module (no `config/firebase` import) so
 * that consumers which only need the context — e.g. OfflineIndicator reading
 * `graceMode` — don't transitively pull in Firebase (and its eager
 * `getAnalytics()` init, which fails under jsdom in tests). `AuthProvider`
 * supplies the value; it re-exports `AuthContext` for back-compat.
 */
/**
 * ADR-0017 absolute-session-lifetime UX state:
 * 'none'   — session healthy (or the gate is disabled).
 * 'warn'   — deadline is near; SessionReauthDialog offers an optional re-auth.
 * 'locked' — deadline passed (or the API returned SESSION_EXPIRED); the dialog
 *            blocks until an in-place re-auth succeeds. Never a signOut.
 */
export type SessionExpiryState = 'none' | 'warn' | 'locked';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** A5: true while running on a cached session during an offline grace window. */
  graceMode: boolean;
  /** ADR-0017: proactive re-auth UX state — see SessionExpiryState. */
  sessionExpiry: SessionExpiryState;
  /** Called by SessionReauthDialog after a successful in-place re-auth. */
  onSessionReauthed: () => void;
  /** Counts successful in-place re-auths, so a check that deferred on SESSION_EXPIRED re-runs. */
  reauthCount: number;
  /** Dismiss the proactive warning (re-engages only on lock or next sign-in). */
  dismissSessionWarning: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  graceMode: false,
  sessionExpiry: 'none',
  onSessionReauthed: () => undefined,
  reauthCount: 0,
  dismissSessionWarning: () => undefined,
});
